import type { ChartInfo } from '@compass/client/types/call';
import { Button } from '@components/ui/button';
import { Textarea } from '@components/ui/textarea';
import { Edit2, Loader2, Save } from 'lucide-react';
import React, { useCallback, useEffect, useState } from 'react';

interface EditableChartNotesProps {
  /** The chart info object containing notes */
  chartInfo?: ChartInfo;
  /** The call ID to update */
  callId: string;
  /**
   * Callback to save notes.
   * Note: This is required when canEdit is true. The component will
   * log a runtime error if onSave is not provided when attempting to save.
   */
  onSave?: (callId: string, notes: string) => Promise<void>;
  /** Whether editing is allowed */
  canEdit: boolean;
}

/**
 * Component for displaying and editing chart notes on calls.
 * Shows editable UI when canEdit is true, otherwise displays read-only notes.
 */
export const EditableChartNotes: React.FC<EditableChartNotesProps> = ({
  chartInfo,
  callId,
  onSave,
  canEdit,
}) => {
  const [isEditing, setIsEditing] = useState(false);
  const [editedNotes, setEditedNotes] = useState(chartInfo?.notes || '');
  const [isSaving, setIsSaving] = useState(false);

  // Sync editedNotes with chartInfo.notes when chartInfo changes (e.g., after save)
  useEffect(() => {
    if (!isEditing) {
      setEditedNotes(chartInfo?.notes || '');
    }
  }, [chartInfo?.notes, isEditing]);

  const handleStartEdit = () => {
    setEditedNotes(chartInfo?.notes || '');
    setIsEditing(true);
  };

  const handleCancel = () => {
    setIsEditing(false);
    setEditedNotes(chartInfo?.notes || '');
  };

  const handleSave = useCallback(async () => {
    if (!onSave) {
      console.error('EditableChartNotes: onSave is required when canEdit is true');
      return;
    }
    setIsSaving(true);
    try {
      await onSave(callId, editedNotes);
      setIsEditing(false);
    } finally {
      setIsSaving(false);
    }
  }, [callId, editedNotes, onSave]);

  // Editable view
  if (canEdit) {
    return (
      <div className="bg-secondary p-2 rounded text-xs">
        <div className="flex justify-between items-center mb-1">
          <span className="text-muted-foreground">Reach-out Note:</span>
          {!isEditing && (
            <Button
              variant="ghost"
              size="sm"
              onClick={handleStartEdit}
              className="h-5 w-5 p-0"
              aria-label="Edit reach-out notes"
            >
              <Edit2 className="w-3 h-3" />
            </Button>
          )}
        </div>
        {isEditing ? (
          <div className="space-y-2">
            <Textarea
              value={editedNotes}
              onChange={e => setEditedNotes(e.target.value)}
              className="min-h-[60px] max-w-[400px] text-xs"
              placeholder="Enter reach-out notes..."
              aria-label="Reach-out notes"
            />
            <div className="flex justify-end gap-1">
              <Button
                variant="ghost"
                size="sm"
                onClick={handleCancel}
                disabled={isSaving}
                className="h-6 px-2 text-xs"
              >
                Cancel
              </Button>
              <Button
                variant="default"
                size="sm"
                onClick={handleSave}
                disabled={isSaving}
                className="h-6 px-2 text-xs"
              >
                {isSaving ? (
                  <Loader2 className="w-3 h-3 animate-spin" />
                ) : (
                  <Save className="w-3 h-3 mr-1" />
                )}
                Save
              </Button>
            </div>
          </div>
        ) : (
          <div className="text-foreground whitespace-pre-wrap max-w-[400px] max-h-20 overflow-auto">
            {chartInfo?.notes || (
              <span className="text-muted-foreground italic">No notes yet. Click edit to add.</span>
            )}
          </div>
        )}
      </div>
    );
  }

  // Read-only view - only show if there are notes
  if (!chartInfo?.notes) {
    return null;
  }

  return (
    <div className="bg-secondary p-2 rounded text-xs">
      <div className="text-muted-foreground mb-1">Reach-out Note:</div>
      <div className="text-foreground whitespace-pre-wrap max-w-[400px] max-h-20 overflow-auto">
        {chartInfo.notes}
      </div>
    </div>
  );
};
